import { ReactNode, createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { TabsProps } from './types';

export const Tabs = ({ children, classNames = '', activeTabIndex }: TabsProps) => {
    function findActiveTab(a: any) {
        if (!Array.isArray(a)) {
            return 0;
        }

        return a.reduce((accumulator: any, currentValue: { props: { active: boolean } } | string, i: number) => {
            if (typeof currentValue !== 'string' && currentValue.props.active) {
                return i;
            }

            return accumulator;
        }, 0);
    }

    // TODO: Fix typing.
    function tabValidator(tab: any) {
        return typeof tab !== 'string' && tab.type.displayName === 'Tab';
    }

    const [activeTab, setActiveTab] = useState(findActiveTab(children));

    useEffect(() => {
        if (activeTabIndex) {
            setActiveTab(activeTabIndex);
        }
    }, [activeTabIndex]);

    return (
        <>
            <div className="flex w-full">
                {!Array.isArray(children) && typeof children === 'string' && children}
                {!Array.isArray(children) && typeof children !== 'string' && (
                    <div className={`${classNames}`}>
                        {tabValidator(children) && (
                            <Tab key="tab-{i}" currentTab={0} activeTab={activeTab} setActiveTab={setActiveTab} onClick={children.props.onClick}>
                                {children.props.children}
                            </Tab>
                        )}
                    </div>
                )}
                {/* TODO: Fix typing  */}
                {Array.isArray(children) &&
                    children?.map((item: any, i: number) => (
                        <div className={`${classNames}`}>
                            {tabValidator(item) && (
                                <Tab key="tab-{i}" currentTab={i} activeTab={activeTab} setActiveTab={setActiveTab} onClick={item.props.onClick}>
                                    {item.props.children}
                                </Tab>
                            )}
                            {typeof item === 'string' && item}
                        </div>
                    ))}
            </div>
            <div className="flex-1 flex flex-col">
                {!Array.isArray(children) && typeof children === 'string' && (
                    <div data-testid="tab" className="visible flex-1 flex flex-col">
                        {children}
                    </div>
                )}
                {!Array.isArray(children) && typeof children !== 'string' && (
                    <div data-testid="tab" className="visible flex-1 flex flex-col">
                        {children.props.component}
                    </div>
                )}
                {/* TODO: Fix typing  */}
                {Array.isArray(children) &&
                    children?.map((item: any, i: number) => (
                        <div data-testid="tab" className={` ${i === activeTab ? 'visible' : 'hidden'}`}>
                            {typeof item !== 'string' && item.props.component}
                            {typeof item === 'string' && item}
                        </div>
                    ))}
            </div>
        </>
    );
};

export const Tab = ({ children, activeTab, currentTab, setActiveTab, onClick }: any) => (
    <div
        role="button"
        tabIndex={0}
        aria-hidden="true"
        className={`px-8 py-3 cursor-pointer text-sm font-bold transition-colors ease-in-out duration-200 delay-0 active:text-purple-900 bg-white rounded-t-md text-center
      ${activeTab === currentTab ? 'text-black border-t-2 border-primary' : 'text-gray-500 hover:bg-purple-200 hover:bg-opacity-40'}`}
        onClick={() => {
            setActiveTab(currentTab);
            onClick?.(currentTab);
        }}
    >
        {children}
    </div>
);

Tab.displayName = 'Tab';

enum Direction {
    HORIZONTAL,
    VERTICAL
}

interface TabContextValue {
    id: string;
    direction: Direction;
}

export const TabContext = createContext<TabContextValue>({
    id: 'Please provide a unique id',
    direction: Direction.HORIZONTAL
});

interface TabGroupProps {
    children: ReactNode;
    direction?: Direction;
}

export function TabGroup({ direction = Direction.HORIZONTAL, children }: TabGroupProps) {
    const id = useId();

    const value = useMemo(
        () => ({
            id,
            direction
        }),
        [direction, id]
    );

    return <TabContext.Provider value={value}>{children}</TabContext.Provider>;
}

interface TabProps {
    index: number;
    className: string;
    activeClassName: string;
    activeTab: number;
    setActiveTab(tab: number): void;
    trigger?: 'click' | 'hover' | 'none';
    children?: ReactNode;
    [key: string]: any;
}

export function TabCom({ index, className, activeClassName, activeTab, setActiveTab, trigger = 'click', ...props }: TabProps) {
    const { id } = useContext(TabContext);
    const tabRef = useRef<HTMLButtonElement>(null);
    const isMounted = useRef(false);

    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            return;
        }
        if (activeTab === index) {
            tabRef.current?.focus({ preventScroll: true });
        }
    }, [activeTab, index]);

    const calculatedClassName = useMemo(
        () => [className, activeTab === index ? activeClassName : ''].join(' '),
        [className, activeClassName, activeTab, index]
    );

    const interactionProps = useMemo(() => {
        const interactions: any = {};

        interactions.onClick = () => {
            setActiveTab(index);
        };

        if (trigger === 'hover') {
            interactions.onMouseEnter = () => setActiveTab(index);
        }

        return interactions;
    }, [trigger, setActiveTab, index]);

    return (
        <span
            ref={tabRef}
            role="tab"
            type="button"
            aria-controls={`${id}-${index}`}
            aria-selected={activeTab === index}
            tabIndex={activeTab === index ? 0 : -1}
            className={calculatedClassName}
            {...interactionProps}
            {...props}
        />
    );
}

interface TabPanelProps {
    index: number;
    className: string;
    activeClassName: string;
    inactiveClassName: string;
    activeTab: number;
    setActiveTab(tab: number): void;
    children?: ReactNode;
    [key: string]: any;
}

export function TabPanel({ index, className, activeClassName, inactiveClassName, activeTab, setActiveTab, ...props }: TabPanelProps) {
    const { id } = useContext(TabContext);

    const calculatedClassName = useMemo(
        () => [className, activeTab === index ? activeClassName : inactiveClassName].join(' '),
        [className, activeClassName, inactiveClassName, activeTab, index]
    );

    return (
        <div
            style={{
                ...props.style,
                visibility: activeTab === index ? 'visible' : 'hidden'
            }}
            id={`${id}-${index}`}
            role="tabpanel"
            className={`${calculatedClassName}`}
            {...props}
        />
    );
}

interface TabListProps {
    activeTab: number;
    setActiveTab(tab: number): void;
    numTabs: number;
    children?: ReactNode;
    classNames?: string;
    [key: string]: any;
}

export function TabList({ setActiveTab, numTabs, activeTab, classNames, ...props }: TabListProps) {
    const tabListRef = useRef<any>(null);
    const { direction } = useContext(TabContext);

    const handleKeyUp = useCallback(
        (e: KeyboardEvent) => {
            const actions: Record<string, () => void> = {
                End: () => {
                    setActiveTab(numTabs - 1);
                },
                Home: () => {
                    setActiveTab(0);
                }
            };

            if (direction === Direction.HORIZONTAL) {
                actions.ArrowLeft = () => {
                    const previous = activeTab - 1 >= 0 ? activeTab - 1 : numTabs - 1;
                    setActiveTab(previous);
                };

                actions.ArrowRight = () => {
                    const next = activeTab + 1 < numTabs ? activeTab + 1 : 0;
                    setActiveTab(next);
                };
            }

            if (direction === Direction.VERTICAL) {
                actions.ArrowUp = () => {
                    const previous = activeTab - 1 >= 0 ? activeTab - 1 : numTabs - 1;
                    setActiveTab(previous);
                };

                actions.ArrowDown = () => {
                    const next = activeTab + 1 < numTabs ? activeTab + 1 : 0;
                    setActiveTab(next);
                };
            }

            const action = actions[e.key];

            if (action) {
                e.preventDefault();
                action();
            }
        },
        [setActiveTab, activeTab, numTabs, direction]
    );

    useEffect(() => {
        tabListRef?.current?.addEventListener('keyup', handleKeyUp);

        return () => tabListRef?.current?.removeEventListener('keyup', handleKeyUp);
    }, [handleKeyUp]);

    // @ts-ignore
    return <div ref={tabListRef} role="tablist" className={`@container/tabs flex ${classNames || ''}`} {...props} />;
}
TabGroup.Tab = TabCom;
TabGroup.TabPanel = TabPanel;
TabGroup.TabList = TabList;
TabGroup.direction = Direction;
